import * as Sentry from '@sentry/react';
import { ENV } from '@config/env';

/**
 * Initialise Sentry for the main (rehabitair-project) app.
 *
 * Reads config through `ENV` (config/env.js): in a built image the DSN, environment
 * and sample rate are injected at container start, the release is baked in.
 *
 * No-ops when there is no DSN or when running the Vite dev server, so local
 * development never ships noise to Sentry.
 */
export function initSentry() {
  const dsn = ENV.sentryDsn;
  // Disabled in the Vite dev server by default (avoids dev noise). Set
  // VITE_SENTRY_ENABLE_IN_DEV=true in .env to force-enable it for local testing.
  const enableInDev = import.meta.env.VITE_SENTRY_ENABLE_IN_DEV === 'true';
  if (!dsn || (import.meta.env.DEV && !enableInDev)) {
    return;
  }

  Sentry.init({
    dsn,
    environment: ENV.sentryEnvironment || ENV.name,
    release: ENV.sentryRelease || undefined,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({ maskAllText: true, blockAllMedia: true }),
    ],
    // Performance tracing sample rate, configurable via env (default 20%).
    tracesSampleRate: Number(ENV.sentryTracesSampleRate || 0.2),
    // Session Replay: never record healthy sessions, always record when an
    // error occurs (so we get the lead-up to a crash without the volume cost).
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 1,
  });
}
